import { useEffect, useId, useLayoutEffect, useRef, useState, type FocusEvent, type MouseEvent, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'

// Tooltip mechanics shared by every InfoTooltip composition (I5 §17.1): see
// Term.tsx for the rules. Kept apart so the component file stays
// fast-refresh friendly.

const DELAY = 300
const GAP = 8

export interface TooltipPlacement {
  top: number
  left: number
}

// Hook form: for triggers that are themselves links or cards (an `<a>` cannot
// contain a `<button>`). Returns the props to spread on the trigger and the
// portal node to render beside it.
export function useInfoTooltip<T extends HTMLElement | SVGElement>({ content, width = 280, delay = DELAY, onOpen, testId, disabled }: { content: ReactNode; width?: number; delay?: number; onOpen?: () => void; testId?: string; disabled?: boolean }) {
  const id = useId()
  const ref = useRef<T>(null)
  const tipRef = useRef<HTMLDivElement>(null)
  const timer = useRef<number | null>(null)
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<TooltipPlacement | null>(null)

  const show = () => {
    if (disabled) return
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => {
      setPos(null)
      setOpen(true)
      onOpen?.()
    }, delay)
  }
  const hide = () => {
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = null
    setOpen(false)
  }
  // Place after the first paint so the real height is known: above when it
  // fits, else below; never over the trigger. Horizontally within the viewport.
  useLayoutEffect(() => {
    if (!open) return
    const r = (ref.current as Element | null)?.getBoundingClientRect()
    const t = tipRef.current?.getBoundingClientRect()
    if (!r || !t) return
    const h = t.height
    const w = Math.min(width, window.innerWidth - 16)
    const left = Math.min(Math.max(8, r.left + r.width / 2 - w / 2), window.innerWidth - w - 8)
    const above = r.top - GAP - h
    const below = r.bottom + GAP
    const top = above >= 8 ? above : below + h <= window.innerHeight - 8 ? below : Math.max(8, above)
    setPos({ top, left })
  }, [open, width])
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && hide()
    const onScroll = () => hide()
    window.addEventListener('keydown', onKey)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open])
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current) }, [])

  const triggerProps = {
    ref: ref as unknown as RefObject<T>,
    'aria-describedby': open ? id : undefined,
    onMouseEnter: (_e: MouseEvent) => show(),
    onMouseLeave: (_e: MouseEvent) => hide(),
    onFocus: (_e: FocusEvent) => show(),
    onBlur: (_e: FocusEvent) => hide(),
  }
  const node = open
    ? createPortal(
        <div id={id} ref={tipRef} role="tooltip" className="rd rd-tooltip" data-testid={testId} style={{ top: pos?.top ?? -9999, left: pos?.left ?? 0, maxWidth: width, width: width > 280 ? width : undefined, visibility: pos ? 'visible' : 'hidden' }}>
          {content}
        </div>,
        document.body
      )
    : null
  return { triggerProps, node, open, hide }
}

